// Button zoom walks these levels; wheel zoom is continuous between the first and last.
const LEVELS = [0.4, 0.5, 0.6, 0.75, 0.9, 1, 1.25, 1.5, 2, 2.5, 3];
const MIN_ZOOM = LEVELS[0];
const MAX_ZOOM = LEVELS[LEVELS.length - 1];
const DEFAULT_ZOOM = 0.9;
// Scroll pixels to double the zoom. Lower it to make the wheel more sensitive.
const PIXELS_PER_DOUBLING = 400;
// A notch is 3 lines, and should land near the ~100px a pixel-mode browser reports for one.
const PIXELS_PER_LINE = 33;
const PIXELS_PER_PAGE = 400;
// A mouse-wheel notch reports around 100px, a pinch single digits, so one sensitivity
// can't serve both. Deltas below this are treated as a pinch and scaled up.
const FINE_SCROLL_MAX = 50;
const FINE_SCROLL_BOOST = 3;
// The whole 0.4-3 range is under three doublings, so an unlucky boosted flick could
// otherwise cross a third of it in one event.
const MAX_PIXELS_PER_EVENT = 150;

// A run of wheel events with no gap this long counts as a single gesture.
const GESTURE_IDLE_MS = 200;

// Wheel zoom lands on values like 0.9000000000000001, which would otherwise let a button
// "step" to the level it is already sitting on.
const LEVEL_EPSILON = 0.001;

// Movement below this is a click, above it a drag whose trailing click gets suppressed.
const DRAG_THRESHOLD_PX = 4;

const DESCENDING_LEVELS = [...LEVELS].reverse();

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

// deltaY arrives in pixels, lines or pages depending on browser, platform and device.
function scrollPixels({ deltaY, deltaMode }) {
  if (deltaMode === 1) return deltaY * PIXELS_PER_LINE;
  if (deltaMode === 2) return deltaY * PIXELS_PER_PAGE;

  return deltaY;
}

// Only a pixel-mode delta can be a pinch: line and page modes are always a real wheel, and
// their raw deltaY is small enough (Firefox reports 3 lines a notch) to look like one.
function fineScroll({ deltaY, deltaMode }) {
  return deltaMode === 0 && Math.abs(deltaY) < FINE_SCROLL_MAX;
}

export const DocumentPanZoom = {
  mounted() {
    this.zoom = DEFAULT_ZOOM;
    this.buttons = this.el.querySelectorAll("[data-zoom]");
    this.gesturing = false;
    this.fineGesture = false;
    this.drag = null;
    this.swallowClick = false;

    // Capture, so the swallow below beats the phx-click on whatever word the drag landed on.
    this.el.addEventListener(
      "click",
      (event) => {
        if (this.swallowClick) {
          this.swallowClick = false;
          event.stopPropagation();
          event.preventDefault();
          return;
        }

        const button = event.target.closest("[data-zoom]");
        if (button) this.stepToLevel(button.dataset.zoom);
      },
      { capture: true }
    );

    this.el.addEventListener("wheel", (event) => this.onWheel(event), { passive: false });
    this.el.addEventListener("pointerdown", (event) => this.onPointerDown(event));
    this.el.addEventListener("pointermove", (event) => this.onPointerMove(event));
    this.el.addEventListener("pointerup", () => this.endDrag());
    this.el.addEventListener("pointercancel", () => this.endDrag());

    this.apply();
  },

  updated() {
    if (this.el.style.getPropertyValue("--doc-zoom")) return;

    this.apply();
  },

  destroyed() {
    clearTimeout(this.gestureTimer);
  },

  // Freespace is marked in the templates. `target` must be the surface itself: a hit on a
  // child means the user aimed at a word, a highlight or a flyout.
  panSurface(target) {
    return target.hasAttribute?.("data-pan-surface");
  },

  onPointerDown(event) {
    // Whatever click the last drag was owed has been and gone.
    this.swallowClick = false;

    // Touch already pans natively; handling it here would scroll twice as fast.
    if (event.pointerType === "touch" || event.button !== 0 || !this.panSurface(event.target)) return;

    this.drag = {
      x: event.clientX,
      y: event.clientY,
      scrollLeft: this.el.scrollLeft,
      scrollTop: this.el.scrollTop,
      moved: false
    };

    this.el.setPointerCapture(event.pointerId);
    // On press, not on threshold: the cursor should confirm the grab immediately, and
    // select-none only prevents a selection if it lands before the compat mousedown.
    this.el.toggleAttribute("data-dragging", true);
  },

  onPointerMove(event) {
    if (!this.drag) return;

    // making sure the drag ends if the button is released
    if (event.buttons === 0) return this.endDrag();

    const dx = event.clientX - this.drag.x;
    const dy = event.clientY - this.drag.y;

    // Below the threshold this is still a click, and mousedown's cancel_selection stands.
    if (!this.drag.moved) {
      if (Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;

      this.drag.moved = true;
    }

    this.el.scrollLeft = this.drag.scrollLeft - dx;
    this.el.scrollTop = this.drag.scrollTop - dy;
  },

  // Pointer capture releases itself on pointerup and pointercancel, so there is nothing to
  // undo here beyond our own state.
  endDrag() {
    if (!this.drag) return;

    // A drag that ends over a word must not also click it. Should no click follow at all -
    // a cancelled gesture, a release outside the window - the next press clears this.
    this.swallowClick = this.drag.moved;
    this.drag = null;
    this.el.toggleAttribute("data-dragging", false);
  },

  onWheel(event) {
    if (!event.ctrlKey && !event.metaKey) return;

    event.preventDefault();

    if (this.startOfGesture()) {
      this.fineGesture = fineScroll(event);
      this.closeFlyouts();
    }

    const boosted = scrollPixels(event) * (this.fineGesture ? FINE_SCROLL_BOOST : 1);
    const pixels = clamp(boosted, -MAX_PIXELS_PER_EVENT, MAX_PIXELS_PER_EVENT);

    // Exponential, so a given scroll distance is the same perceived change at any zoom.
    this.zoomTo(this.zoom * 2 ** (-pixels / PIXELS_PER_DOUBLING), this.pointerOffset(event));
  },

  stepToLevel(direction) {
    const target = direction === "reset" ? DEFAULT_ZOOM : this.nextLevel(direction);

    if (target === undefined || target === this.zoom) return;

    this.closeFlyouts();
    this.zoomTo(target, this.paneCentre());
  },

  zoomTo(zoom, offset) {
    const next = clamp(zoom, MIN_ZOOM, MAX_ZOOM);

    if (next === this.zoom) return;

    this.zoom = next;

    const anchor = this.anchor(offset);
    this.apply();
    this.restore(anchor);
  },

  nextLevel(direction) {
    return direction === "in"
      ? LEVELS.find((level) => level > this.zoom + LEVEL_EPSILON)
      : DESCENDING_LEVELS.find((level) => level < this.zoom - LEVEL_EPSILON);
  },

  // Deltas ramp up over a flick, so classifying per event would cross the pinch threshold
  // partway through and change the zoom rate under the user's fingers.
  startOfGesture() {
    const started = !this.gesturing;

    this.gesturing = true;
    clearTimeout(this.gestureTimer);
    this.gestureTimer = setTimeout(() => (this.gesturing = false), GESTURE_IDLE_MS);

    return started;
  },

  // Flyout positions are computed on mount/update only, so a stale one can't follow the
  // highlight it points at. This pushes a server event, so a continuous zoom closes them
  // once per gesture rather than once per wheel event.
  closeFlyouts() {
    liveSocket.execJS(this.el, this.el.dataset.cancelSelectionEvent);
  },

  apply() {
    const zoomedOut = this.zoom < 1;

    this.el.style.setProperty("--doc-zoom", this.zoom);
    this.el.style.setProperty("--doc-zoom-align", zoomedOut ? "center" : "start");
    this.el.style.setProperty("--doc-zoom-contain", zoomedOut ? "layout" : "layout paint");

    this.syncButtons();
    this.syncPannable();
  },

  syncPannable() {
    const pannable =
      this.el.scrollWidth > this.el.clientWidth || this.el.scrollHeight > this.el.clientHeight;

    this.el.toggleAttribute("data-pannable", pannable);
  },

  pointerOffset(event) {
    const rect = this.el.getBoundingClientRect();

    return { x: event.clientX - rect.left, y: event.clientY - rect.top };
  },

  paneCentre() {
    return { x: this.el.clientWidth / 2, y: this.el.clientHeight / 2 };
  },

  // Only the content box of the zoomed element actually scales - its padding stays a fixed
  // number of pixels, and so does the gap between pages. Anchoring on the scroller's full
  // scroll size instead would drift by that padding, ~190px a step in the widest layout.
  contentBox() {
    const box = this.el.querySelector("[data-zoom-content]");
    const { paddingLeft, paddingRight, paddingTop, paddingBottom } = getComputedStyle(box);
    const left = parseFloat(paddingLeft);
    const top = parseFloat(paddingTop);

    return {
      left: box.offsetLeft + left,
      top: box.offsetTop + top,
      width: box.clientWidth - left - parseFloat(paddingRight),
      height: box.clientHeight - top - parseFloat(paddingBottom)
    };
  },

  // The content point at `offset` from the pane's corner, as a fraction of that box.
  anchor({ x, y }) {
    const { scrollLeft, scrollTop } = this.el;
    const box = this.contentBox();

    return {
      fx: (scrollLeft + x - box.left) / box.width,
      fy: (scrollTop + y - box.top) / box.height,
      x,
      y
    };
  },

  restore({ fx, fy, x, y }) {
    const box = this.contentBox();

    this.el.scrollLeft = box.left + fx * box.width - x;
    this.el.scrollTop = box.top + fy * box.height - y;
  },

  syncButtons() {
    const disabled = {
      in: this.zoom >= MAX_ZOOM,
      out: this.zoom <= MIN_ZOOM,
      reset: this.zoom === DEFAULT_ZOOM
    };

    this.buttons.forEach((button) => {
      button.setAttribute("aria-disabled", disabled[button.dataset.zoom]);
    });
  }
};
